import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
} from '@denishussar/html5-game-engine';
import { GameModel } from '../../Models/Game/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { TaskModel, TaskType } from '../../Models/Game/TaskModel';
import { secondsToMMSS } from '../../Common/Utils';
import { MiniGameTypeEnum } from '../../Models/Game/MiniGameModel';
import { ModelEvents } from '@denishussar/html5-game-engine/dist';
import { AchievementType } from '../../Models/Game/AchievementModel';

class UITaskController extends AnimationBaseController {
    $model: TaskModel;

    public constructor(@Inject private _options) {
        super('UITaskController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            modelWatch: {
                time: (value: number) => {
                    if (this.$model) {
                        this.updateText(value, this.$model.text);
                    }
                },
                text: (value: string) => {
                    if (this.$model) {
                        this.updateText(this.$model.time, value);
                    }
                },
                index: (value: number) => {
                    if (this.$model) {
                        this.gotoAndStopByQuery(
                            'icon',
                            this.$model.type === TaskType.Global ? 0 : 1,
                            false
                        );
                        this.setTextByQuery('icon.text', value.toString());
                    }
                },
            },
            modelEvents: {
                update: () => {
                    if (this.$model) {
                        this.updateText(this.$model.time, this.$model.text);
                    }
                },
            },
        };
    }

    protected onInitialize(): void {
        this.updateText(this.$model.time, this.$model.text);

        this.gotoAndStopByQuery(
            'icon',
            this.$model.type === TaskType.Global ? 0 : 1,
            false
        );
        this.setTextByQuery('icon.text', this.$model.index.toString());
    }

    protected onDestroy() {
        this.setTextByQuery('text', '');
    }

    private updateText(time: number, text: string): void {
        if (this.$model.type === TaskType.Regular) {
            this.setTextByQuery('text', `(${secondsToMMSS(time)}) ${text}`);
        } else {
            this.setTextByQuery('text', `(----:----) ${text}`);
        }
    }
}

export class UITaskListController extends AnimationBaseController {
    $model: GameModel;

    public constructor(
        @Inject private _options,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents
    ) {
        super('UITaskListController');
    }
    private _opennessStart: boolean = false;
    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            modelEvents: {
                miniGameTimeOver: (miniGameType: MiniGameTypeEnum) => {
                    this.clearTasks();
                    this.updateTasks();
                    
                },
                miniGameComplete: (miniGameType: MiniGameTypeEnum) => {
                    this.clearTasks();
                    this.updateTasks();
                },
                addTask: (miniGameType: MiniGameTypeEnum) => {
                    this.clearTasks();
                    this.updateTasks();
                    this._isGlobalAchievement(miniGameType);
                },
                miniGameStart: (miniGameType: MiniGameTypeEnum) => {
                    const globalTask = this.$model.taskCurrentList.where(
                        o => o.miniGameType === miniGameType && o.type === TaskType.Global
                    );
                    if (globalTask.count()) {
                        this._opennessStart = true
                    }
                },
            },
            queue: {
                openness: {
                    condition: (clock: number) => clock > 10,
                    action: (time, miniGameType: MiniGameTypeEnum) => {
                        if (!this._opennessStart) {
                            this._gameLogic.achievementOpennessFail()
                        } else {
                            this._opennessStart = false
                        }
                    },
                },
            },
        };
    }

    protected onInitialize(): void {
        this.clearTasks();
        this.updateTasks();
    }

    protected onResize(width: number, height: number, scale: number): void {
        this.x = -width / scale / 2;
    }

    private clearTasks(): void {
        for (let i = 0; i < this.$model.taskCount; i++) {
            this.removeChildControllerByName(`task${i + 1}`);
        }
    }

    private updateTasks(): void {
        const activeTasks = this.$model.taskCurrentList
            .where(o => o.active)
            .orderBy(o => o.index);
        const hasGlobal = !!activeTasks.first(o => o.type === TaskType.Global);

        for (let i = 0; i < activeTasks.count(); i++) {
            const key = `task${i + 1}`;
            const model = activeTasks.elementAt(i);

            this.addChildController(
                {
                    instanceName: key,
                    view: UITaskController,
                    model: model,
                },
                key
            );

            this.$childControllers.get<UITaskController>(key).alpha = hasGlobal
                ? model.type === TaskType.Global
                    ? 1
                    : 0.5
                : 1;
        }
    }

    private _isGlobalAchievement(miniGameType: MiniGameTypeEnum): void {
        const globalTask = this.$model.taskCurrentList.where(
            o => o.miniGameType === miniGameType && o.type === TaskType.Global
        );

        if (globalTask.count()) {
            this.$queue.get('openness').addToQueue(4, miniGameType);
        }
    }
}
